import { inject, Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { firstValueFrom, Subject, takeUntil } from 'rxjs';
import { AssetDowntimeInsights, KpiInsightsResponse, MaintenanceCostByAssetResponse, MaintenanceInsightsResponse, NextMaintenanceResponse } from '../models/maintenance-dashboard-response.model';
import { AccountDateFilterRequest } from '../../../../_shared/models/accountDateFilter.model';
import { SummaryRequest } from '../../../../_shared/models/summary-request.model';
import { AccountFilterRequest } from '../../../../_shared/models/account-filter.model';
import { Response } from '../../../../_shared/models/response.model';
import { KpiStatsResponse } from '../../../../_shared/models/kpi-response.model';
import { PaginationRequest } from '../../../../_shared/models/pagination-request.model';
import { Pagination } from '../../../../_shared/models/pagination-response.model';
import { environment } from '../../../../../environments/environment';
import {
  AssetDowntimeRequest,
  MaintenanceCostByAssetRequest,
  PreventiveVsReactiveRequest,
  PreventiveVsReactiveSummaryRequest
} from '../models/maintenance-dashboard-request.model';

@Injectable({
  providedIn: 'root'
})
export class MaintenanceDashboardService {
  private httpClient = inject(HttpClient);

  async getPreventiveVsReactiveSummary(request: PreventiveVsReactiveSummaryRequest, cancelSubject: Subject<void>): Promise<Response<KpiInsightsResponse>> {
    return await firstValueFrom(
      this.httpClient.post(`${environment.apiEndpoint}/maintenancedashboard/preventive-vs-reactive-summary`, request).pipe(takeUntil(cancelSubject))
    );
  }

  async getUnplannedRepairsSummary(request: SummaryRequest, cancelSubject: Subject<void>): Promise<Response<KpiInsightsResponse>> {
    return await firstValueFrom(
      this.httpClient.post(`${environment.apiEndpoint}/maintenancedashboard/unplanned-repairs-kpi`, request).pipe(takeUntil(cancelSubject))
    );
  }

  async getUpcomingMaintenanceSummary(request: SummaryRequest, cancelSubject: Subject<void>): Promise<Response<KpiInsightsResponse>> {
    return await firstValueFrom(
      this.httpClient.post(`${environment.apiEndpoint}/maintenancedashboard/upcoming-maintenance-kpi`, request).pipe(takeUntil(cancelSubject))
    );
  }

  async getMaintenanceCostYtdSummary(request: AccountFilterRequest, cancelSubject: Subject<void>): Promise<Response<KpiInsightsResponse>> {
    return await firstValueFrom(
      this.httpClient.post(`${environment.apiEndpoint}/maintenancedashboard/maintenance-cost-ytd-kpi`, request).pipe(takeUntil(cancelSubject))
    );
  }

  async getPreventiveRatio(request: PreventiveVsReactiveRequest, cancelSubject: Subject<void>): Promise<Response<MaintenanceInsightsResponse[]>> {
    return await firstValueFrom(
      this.httpClient.post(`${environment.apiEndpoint}/maintenancedashboard/preventive-ratio`, request).pipe(takeUntil(cancelSubject))
    );
  }

  async getReactiveRatio(request: PreventiveVsReactiveRequest, cancelSubject: Subject<void>): Promise<Response<MaintenanceInsightsResponse[]>> {
    return await firstValueFrom(
      this.httpClient.post(`${environment.apiEndpoint}/maintenancedashboard/reactive-ratio`, request).pipe(takeUntil(cancelSubject))
    );
  }

  async getMissedServiceInsights(request: AccountDateFilterRequest, cancelSubject: Subject<void>): Promise<Response<MaintenanceInsightsResponse[]>> {
    return await firstValueFrom(
      this.httpClient.post(`${environment.apiEndpoint}/maintenancedashboard/assets-in-maintenance-missed-service-ratio`, request).pipe(takeUntil(cancelSubject))
    );
  }

  async getCompletedServiceInsights(request: AccountDateFilterRequest, cancelSubject: Subject<void>): Promise<Response<MaintenanceInsightsResponse[]>> {
    return await firstValueFrom(
      this.httpClient.post(`${environment.apiEndpoint}/maintenancedashboard/assets-in-maintenance-completed-service-ratio`, request).pipe(takeUntil(cancelSubject))
    );
  }

  async getAssetsInMaintenanceSummary(request: SummaryRequest, cancelSubject: Subject<void>): Promise<Response<KpiStatsResponse>> {
    return await firstValueFrom(
      this.httpClient.post(`${environment.apiEndpoint}/maintenancedashboard/assets-in-maintenance-kpi`, request).pipe(takeUntil(cancelSubject))
    );
  }

  async getAssetDowntimeData(request: AssetDowntimeRequest, cancelSubject: Subject<void>): Promise<Response<AssetDowntimeInsights[]>> {
    return await firstValueFrom(
      this.httpClient.post(`${environment.apiEndpoint}/maintenancedashboard/asset-downtime-time-ratio`, request).pipe(takeUntil(cancelSubject))
    )
  }

  async getAssetDowntimeKpi(request: SummaryRequest, cancelSubject: Subject<void>): Promise<Response<KpiStatsResponse>> {
    return await firstValueFrom(
      this.httpClient.post(`${environment.apiEndpoint}/maintenancedashboard/asset-downtime-kpi`, request).pipe(takeUntil(cancelSubject))
    );
  }

  async getMaintenanceCostByAsset(request: MaintenanceCostByAssetRequest, cancelSubject: Subject<void>): Promise<Response<MaintenanceCostByAssetResponse[]>> {
    return await firstValueFrom(
      this.httpClient.post(`${environment.apiEndpoint}/maintenancedashboard/maintenance-cost-by-asset`, request).pipe(takeUntil(cancelSubject))
    );
  }

  async getMaintenanceCostByAssetKpi(request: SummaryRequest, cancelSubject: Subject<void>): Promise<Response<KpiStatsResponse>> {
    return await firstValueFrom(
      this.httpClient.post(`${environment.apiEndpoint}/maintenancedashboard/maintenance-cost-kpi`, request).pipe(takeUntil(cancelSubject))
    );
  }

  async getNextMaintenance(request: PaginationRequest, cancelSubject: Subject<void>): Promise<Response<Pagination<NextMaintenanceResponse[]>>> {
    return await firstValueFrom(
      this.httpClient.post(`${environment.apiEndpoint}/maintenancedashboard/next-maintenance-summary`, request).pipe(takeUntil(cancelSubject))
    );
  }
}